<script setup lang="ts">
import { usePaymentStore } from "@/components/payment/usePaymentStore";
import { useDiscountCode } from "@/store/useDiscountCode";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import { useTodPricing } from "@/store/useTodPricing";
import { useUserModeChange } from "@/store/useUserModeChange";
import { allowedDatesRecurring, doesUserHaveCoupons, formatDurationLabel, getMaxActivityVisitors, getMembershipDiscounts, handleDateTimeError, handleMembersError, mapSelectionToMinutes, useInactivityTimer } from "@/utils/BookingStepperUtil";
import { formatPrice, getBookingStatus, Round2Num } from "@/utils/Formatting";
import { useLocationStore } from "@/utils/LocationStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import { emailValidator, mobileValidator, requiredValidator } from "@validators";
import { cloneDeep, isEmpty } from "lodash";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import { VForm } from "vuetify/components/VForm";
import ActivityStep from "./steps/ActivityStep.vue";
import DateTimeStep from "./steps/DateTimeStep.vue";
import MembersStep from "./steps/MembersStep.vue";
import NumberInPartyStep from "./steps/NumberInPartyStep.vue";
import AgreementStep from "./steps/AgreementStep.vue"
import type { BookingEmit, BookingFormData } from "./types/index";
import { useBooking } from "./useBooking";

const userMode = useUserModeChange();
const { isUserMode } = storeToRefs(userMode);

const props = withDefaults(
  defineProps<{
    isDrawerOpen: boolean;
    areaList?: any[];
    memberList?: any[];
    isQuickBooking?: boolean;
    currentLocation?: string;
    initialStep?: number;
    userId?: string;
    couponValue?: number;
    defaultArea?: string | null;
    currentLoggedInRoles?: any[];
    membership?: any;
    activityList?: any[];
    location?: any;
    loggedUser?: any;
  }>(),
  {
    currentLoggedInRoles: () => [
      {
        name: "Admin",
        location: "default-location",
      },
    ],
  }
);

// Emit
const emit = defineEmits<BookingEmit>();

// Store
const userStore = useUserStore();
const locationStore = useLocationStore();
const globalErrorStore = useGlobalErrorStore();
const paymentStore = usePaymentStore();
const discount = useDiscountCode();
const userRole = useUserRole();
const todStore = useTodPricing();
const { handleUpdateErrorMessage } = globalErrorStore;
const { getItemTOD } = todStore;
const { currentLocationDetails, currentLocation } = storeToRefs(userStore);
const { locationList } = storeToRefs(locationStore);
const { globalErrorMessage, isBookingWarningDialogVisible, errorDateList } = storeToRefs(globalErrorStore);
const { docketId, docketLineId, isPendingBooking } = storeToRefs(paymentStore);

const activityList = ref(props.activityList.filter(act => !act?.futureBookingDisabled) || []);
const { discountCode, discountCodeData } = storeToRefs(discount);
const { getUserCreditCards } = userStore;
const docketBackId = ref<string>("");
const { permissionListMap, userInfo } = storeToRefs(userRole);
const { userCreditCards } = storeToRefs(userStore)

// composable
const booking = useBooking();

const {
  alternativeAreaList,
  getDuration,
  reserveSlot,
  removeExistingPendingBooking,
  updateBooking,
  refetchBooking,
  getAvailableHoursForDay,
} = booking;

// Router
// Refs for step components
const route = useRoute();
const router = useRouter();
const dateTimeStepRef = ref();
const membersStepRef = ref();
const activityStepRef = ref();
const numberInPartyFormRef = ref<any>(null);

// Data
const bookingProp:any = ref({})
const showPayment = ref(true)
const selectedPaymentTab = ref("")
const userHasCoupons = ref(false)
const userMembershipDiscounts = ref({})
const isLoading = ref<boolean>(false);
const isAdvancing = ref<boolean>(false);
const isAdmin = ref(false)
// Set initial step based on whether we show activity selection
const currentStep = ref(props.initialStep);

const form = ref<BookingFormData>({
  id: "",
  title: "",
  date: moment().format("YYYY-MM-DD"),
  activity: activityList.value.length > 0 ? activityList.value[0]?.id : "",
  guests: [],
  members: [],
  area: null,
  time: null,
  duration: 0,
  actualStartTime: null,
  notes: "",
  visitorEmail: "",
  visitorPhone: "",
  owner: "",
  actualDuration: null,
  selectedOptions: [],
  agreement:false,
  numberInParty: 1,
});
const maxPlayers = ref<number>(0);
const role = ref<string>("Member");
const pendingBookingId = ref<any>("");
const showPopup = ref<boolean>(false);
const isCancelDialogVisible = ref<boolean>(false);
const selectedDurationSlot = ref<number>(0);
const isLeftHandedWarningDialogVisible = ref<boolean>(false);
const isLeftHandedBookingErrorVerified = ref<boolean>(false);
const isBookingConfirmDialogVisible = ref<boolean>(false);
const docketLineItems = ref<any[]>([]);
const processingPayment = ref<any>({
  isVisible: false,
  message: "",
  type: "",
  spinner: false,
});
const editBooking = ref<any>({});
const email = ref<string>("");
const refVFormReceipt = ref<any>();
const surchargeAmount = ref<number>(0);

// API-driven booking calculation values
const todAdjustment = ref<number>(0);
const todSource = ref<string>("");
const docketDiscount = ref<number>(0);
const discountApplied = ref<string>("None");
const lineDiscountTotal = ref<number>(0);
const dueAmount = ref<number>(0);
const bookingTotal = ref<number>(0);

// computed
const bookingSteps = computed(() => [
  {
    id: 1,
    title: "Activity",
    subtitle: "",
    icon: "tabler-activity",
    size: 30,
    isValid: true,
  },
  {
    id: 2,
    title: "Date & Time",
    subtitle: "",
    icon: "tabler-calendar",
    size: 30,
    isValid: true,
  },
  {
    id: 3,
    title: selectedActivity?.value?.maxPlayers > 1 ? "Options" : "Extras",
    subtitle: "",
    icon: selectedActivity?.value?.maxPlayers > 1 ? "tabler-users" : "mdi-page-next-outline",
    size: 30,
    isValid: true,
  },
    ...(currentLocationDetails.value?.numberInPartyField
    ? [
        {
          id: 6,
          title: "Party Size",
          subtitle: "",
          icon: "tabler-users-group",
          size: 30,
          isValid: true,
        },
      ]
    : []),
  {
    id: 4,
    title: "Agreement",
    subtitle: "",
    icon: "mdi-file-document-edit",
    size: 30,
    isValid: true,
  },
  {
    id: 5,
    title: "Payment",
    icon: "tabler-cards",
    size: 30,
    isValid: false,
  },
]);

// Computed
const isEditBooking = computed(() => Boolean(editBooking.value?.id));

// Example booking space value, can be 15, 30, 45, or 60
const getBookingSpacing = computed(
  () => selectedActivity.value?.sessionDuration || 60
);

const selectedActivity = computed(() =>
  activityList.value?.find((el: any) => el.id === form.value.activity)
);

const subtotal = computed(() =>
  docketLineItems.value?.reduce(
    (sum: any, item: any) => sum + item.itemPrice * item.qty,
    0
  )
);
const validateAndPrint = async (callback: (email: string) => void) => {
  try {
    const { valid } = await refVFormReceipt.value?.validate();
    if (valid) {
      callback(email.value); // Pass email to the callback if the form is valid
    }
  } catch (e) {
    console.error("An error occurred during validation:", e);
  }
};
const emailReceipt = async (email: string) => {
  if (docketBackId.value) {
    const data = {
      docketID: docketBackId.value,
      email: email,
      requiresEmail: true,
      createdBy: props.userId,
    };

    const response = await pb.collection("docketReceipts").create(data);

    if (response) {
      toast.success("Email Receipt Sent", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }
};

const getSurchargeAmount = computed(() => {
  const selectedLocation = locationList.value?.find(
    (l: {
      id: string;
      cardSurchargeFixedFee?: number;
      cardSurchargePercentFee?: number;
    }) => l.id === props.location
  );

  // Destructure with default values to ensure they're numbers
  const { cardSurchargeFixedFee = 0, cardSurchargePercentFee = 0 } =
    selectedLocation || {};

  // Calculate surcharge fee (fixed fee + percentage of the checkout total)
  const surchargeFee =
    cardSurchargeFixedFee + subtotal.value * (cardSurchargePercentFee / 100);

  if (surchargeFee == 0 && surchargeAmount.value > 0) {
    return surchargeAmount.value.toFixed(2);
  }
  // Return the final result formatted to two decimal places
  return surchargeFee.toFixed(2);
});
const totalAmount = computed(() => {
  return (
    Number(cloneDeep(subtotal.value)) -
    Number(cloneDeep(getDiscountedAmount.value)) +
    Number(cloneDeep(getSurchargeAmount.value))
  ).toFixed(2);
});

const getDiscountedAmount = computed(() => {
  let totalAmount = 0;
  let lineTotal = 0;

  for (let i = 0; i < docketLineItems.value.length; i++) {
    const item = docketLineItems.value[i];
    totalAmount += item?.qty * item?.itemPrice;
    lineTotal += Number(item.lineTotal);
  }

  return Round2Num(totalAmount - lineTotal, 2);
});

const getProductDiscount = computed(() => {
  return Math.max(
    discountCodeData.value?.discountProduct || 0,
    userMembershipDiscounts?.value?.discountProduct || 0
  )
});

const haveCreateAccess = computed(() => {
  return permissionListMap?.value?.bookings?.includes("Create") ? true : false;
});

const getAreaList = computed(() => {
  if (!activityList.value?.length) {
    return [];
  }

  if (!form.value.activity) {
    form.value.activity = activityList.value[0].id;
  }

  return props.areaList?.filter((area: any) =>
    area.activities.includes(form.value.activity) &&
    (!area.adminOnlyBookings || isAdmin.value) &&
    (!area.downForMaintenance || isAdmin.value)
  );
});

const getMaxBookAhead = computed(() => {
  if(pb.authStore.model?.roles?.length > 0 && !isUserMode.value){
    return 52
  }
  return currentLocationDetails.value?.maxBookAheadWeeks || 0
});

const isLeftHandWarning = computed(() => {
  // Check if required data is available
  if (!form.value?.area) return false;

  // Find the area object based on the selected area ID
  const selectedArea = props?.areaList?.find(
    (area) => area.id === form.value.area
  );

  // If the selected area cannot accommodate left-handed members
  if (!selectedArea?.canLeftHanded) {
    // Check if any of the selected members is left-handed
    // Return true if a left-handed member is selected
    return props?.memberList?.some(
      (member) => form.value.members.includes(member.id) && member.leftHanded
    );
  }

  // If the selected area can accommodate left-handed members, return false
  return false;
});

const startDate = computed(() => {
  if (form.value?.date && form.value?.time) {
    const formattedDate = moment(form.value?.date);
    const timeParts: any = form.value?.time.split(":");

    formattedDate.hours(timeParts[0]).minutes(timeParts[1]);

    return formattedDate;
  }

  return null;
});

const getBookingGap = computed(() => {
  return selectedActivity.value?.bookingGap || 0;
});

const handlePaymentSuccess = async () => {
  window.setTimeout(async() => {
  try {
    localStorage.removeItem('qbid')
    let bID = form.value?.id || pendingBookingId.value
    const booking = await pb.collection("bookings").getOne(bID);
    showPayment.value = false
    try {
      bookingProp.value = await pb.collection("bookings").getOne(bID, {expand:'docketLine'});
      if(bookingProp.value.expand.docketLine?.docket) {
        bookingProp.value.docketId = bookingProp.value.expand.docketLine?.docket
      }

    } catch(e){}
    showPayment.value = true
    editBooking.value = booking;
    editBooking.value.selectedOptions = booking?.options;

    // Refresh payment data after successful payment
    await fetchPaymentStepData();
    showPopup.value = false;
    pendingBookingId.value = null;


    // Remove the pending booking ID form localStorage
    try {
      localStorage.removeItem('pendingBookingId')
    } catch(e){}
    clearPendingTimer();
    clearInactivityTimer();

    if (editBooking.value.due === 0) {
      localStorage.setItem('qbid',editBooking.value?.id)
      // close booking dialog after 2 if got paid completely

      if(editBooking.value.state == "Confirmed" && canOrderFood.value){
        showPayment.value = false
      } else {
        setTimeout(() => {
          if (!isBookingConfirmDialogVisible.value) {
            emit("closeDialog", false);
          }
        }, 2000);
      }
    }
  } catch (e) {}
  },500)
};

const canOrderFood = ref(false)
const checkIfFoodCanBeOrdered = async () => {
  let checkFood = await pb.collection("products").getFullList(
    {filter: 'canBuyInWebshop=true && notDirectlySold=false && location="' + currentLocation.value + '"', expand: 'category'})
  if (checkFood) {
    checkFood.forEach(item => {
      if(item?.expand?.category?.isFood){
        canOrderFood.value = true
      }
    })
  }
}

const isAfterHours = async(time: string) => {
  const membership = !isAdmin.value ? props.membership : []
  const response = await pb.send('/bbapi/times/getTimeData', {
    method: 'POST',
    body: {
      activity: selectedActivity.value?.id,
      membership: membership || [],
      date: form.value.date,
      time: time
    },
    requestKey: null,
  });
  console.log('getTimeData response', response)
  return !response.allUsers
};

const afterHours = ref(false)

watch(
  () => form.value?.time,
  async (time) => {
    if (time) {
      afterHours.value = await isAfterHours(time)
    } else {
      afterHours.value = false
    }

    userHasCoupons.value = await doesUserHaveCoupons(form.value.owner, afterHours.value, form.value.activity)
  },
  { immediate: true }
)

/**
 * Computes and returns the global info message for the booking form.
 *
 * @returns {Object} - The global info message object with properties: color, icon, message.
 */
const globalInfoMessage = computed(() => {
  // Calculate the total duration in minutes
  const durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );
  const isDue = editBooking.value?.id
    ? editBooking.value?.due
    : dueAmount.value;

  const minDuration = durationTotal - getBookingGap.value;
  const newTimeDisplay = formatDurationLabel(minDuration)

  let couponList = ""
  if(userHasCoupons.value && !isAdmin.value) {
    let couponValue = props?.couponValue ? props?.couponValue : selectedActivity.value?.sessionFullPrice / selectedActivity.value?.couponCost;

    if(selectedActivity?.value?.sessionFullPrice && selectedActivity?.value?.couponCost) {
      if(form.value?.time && afterHours.value){
        couponValue = selectedActivity?.value?.sessionFullPrice / (selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost)
      } else {
        couponValue = selectedActivity?.value?.sessionFullPrice / selectedActivity?.value?.couponCost
      }
    }

    let productTotal = 0;
    let totalDue = dueAmount.value;
    if(selectedProducts.value && selectedProducts.value.length > 0){
      selectedProducts.value.forEach((product:any) => {
        productTotal += product.itemPrice;
        totalDue = totalDue - product.itemPrice;
      })
    }

    const todDiscountVal = todAdjustment.value < 0 ? -todAdjustment.value : 0;
    const discount = todDiscountVal > docketDiscount.value ? todDiscountVal : docketDiscount.value || 0;
    let couponValueAfterDiscount = (couponValue * 1 * (100 - discount)) / 100;
    let quantity = Math.ceil(totalDue / couponValueAfterDiscount)

    if(quantity > 0) {
      const productExtra = productTotal > 0 ? ` + $${productTotal.toFixed(2)}` : "";
      couponList = "(or " + quantity + " coupon" + (quantity > 1 ? "s" : "") + productExtra +" )"
    }
  }

  const dash = dueAmount.value > 0 || couponList != '' ? " - " : "";
  const baseMessage: any = {
    color: "dialogHeaderColor",
    due: dueAmount.value > 0 ? dueAmount.value : '',
    todAdjustment: todAdjustment.value || 0,
    icon: "",
    message: `${couponList} ${dash} ${newTimeDisplay} of ${selectedActivity.value?.bookingShortDescription ? selectedActivity.value?.bookingShortDescription : 'Play or Practice'}
     ${selectedActivity?.value?.maxPlayers > 2 ? 'with up to ' + selectedActivity?.value?.maxPlayers + ' Players' : ''
    }`,
  };

  const cal_currentStep = bookingSteps.value[currentStep.value]?.id;

  if (cal_currentStep === 1) {
    return {
      ...baseMessage,
      message: "Please select an activity",
    };
  }

  if (cal_currentStep === 4 && !form.value.agreement) {
    return {
      color:'error',
      message: "You must accept the Terms and Conditions",
    };
  }

  if (cal_currentStep === 4 || cal_currentStep === 5) {
    const statusColorName = isDue ? "warning" : "dialogHeaderColor";
    const statusIcon = isDue ? "tabler-alert-triangle" : "";

    return {
      ...baseMessage,
      color: statusColorName,
      icon: statusIcon,
      due: '',
      todAdjustment: 0,
      message: isDue
        ? `The Session fee of $${dueAmount.value} ${couponList} has not been paid yet`
        : "The Session has been fully paid.",
    };
  }

  return baseMessage;
});


const handleGetBooking = async (bookingId: string) => {
  const booking = await pb.collection("bookings").getOne(bookingId);
  editBooking.value = booking;
  editBooking.value.selectedOptions = booking?.options;
  if (booking.state === "Confirmed") {
    isBookingConfirmDialogVisible.value = true;
    emit("showConfirmDialog", { booking: editBooking.value, activity: selectedActivity.value });
    showPopup.value = false;
  }

  if (booking?.due == 0 && props?.isQuickBooking && !isBookingConfirmDialogVisible.value) {
    localStorage.setItem('qbid',editBooking.value?.id)
    emit("closeDialog", false);
    history.replaceState(null, '', route.path);
  }
};

const gotToPos = (docketId: string) => {
  /**
   * Function to navigate to the POS list screen.
   *
   * @param {string} docketId - The ID of the docket to navigate to.
   *
   * @emits {closeDialog} - Emits an event to close the booking dialog after navigating to the POS list screen.
   *
   * @returns {void}
   */
  // move user to pos list screen
  router.push({
    name: "pos",
    query: {
      did: docketId,
    },
  });
  setTimeout(() => {
    emit("closeDialog", false);
  }, 1000);
};


const selectedProducts = computed(() => {
  return form.value.selectedOptions?.map((optionId) => {
    const option = selectedActivity.value?.expand?.options?.find(
      (opt: any) => opt.id === optionId
    );

    if (option && option != undefined) {
      const docketDiscount: any = option.maxDiscount === 0
        ? 0
        : option.maxDiscount > 0
          ? Math.min(getProductDiscount.value, option.maxDiscount)
          : getProductDiscount.value;

      // Get todAdjustment for time of booking
      let currentTodDiscount = 0;
      let todSource = "";
      
      // Get todAdjustment for time of booking
      if (form.value.time && form.value.date){
        const hour = form.value.time.split(":")[0];
        const min = form.value.time.split(":")[1];
        const dateTime = moment(form.value.date).hour(parseInt(hour)).minute(parseInt(min));
        const todData = getItemTOD(option, dateTime, props.loggedUser, props.membership, props.location);
        currentTodDiscount = todData.todAdjustment;
        todSource = todData.todSource;
      }

      // Apply TOD increase to price first (positive percentChange = price increase)
      const priceAfterTodIncrease = currentTodDiscount > 0
        ? (option.sellPrice || 0) * (1 + currentTodDiscount / 100)
        : (option.sellPrice || 0);

      const todDiscountValue = currentTodDiscount < 0 ? -currentTodDiscount : 0;
      const finalDiscount = docketDiscount > todDiscountValue ? docketDiscount : todDiscountValue;
      const discountAmount = priceAfterTodIncrease * (finalDiscount / 100);

      const discountApplied = docketDiscount > todDiscountValue ? "Membership" : todDiscountValue > 0 ? "TOD" : "None";

      return {
        id: option?.id,
        name: option?.name,
        docketDiscount: docketDiscount,
        todAdjustment: currentTodDiscount,
        todSource: todSource,
        lineDiscountTotal: discountAmount,
        discountApplied: discountApplied,
        sellPrice: option?.sellPrice,
        itemPrice: priceAfterTodIncrease - discountAmount,
        qty: 1,
      };
    }
  });
});

const assignRole = () => {
  if (!props?.currentLoggedInRoles) {
    role.value = "";
  }

  // Filter the current logged-in user's roles for the selected location
  let currentLocationRole = props?.currentLoggedInRoles?.filter(
    (role) => role.location === props?.currentLocation
  );

  // If the current logged-in user has roles at the selected location
  if (currentLocationRole?.length) {
    // If the current logged-in user has an admin role at the selected location
    if (currentLocationRole.some((role) => role.name?.includes("Admin"))) {
      // Assign the admin role
      role.value = "Admin";
    } else if (
      // If the current logged-in user has a staff role at the selected location
      currentLocationRole.some((role) => role.name?.includes("Staff"))
    ) {
      // Assign the member role
      role.value = "Member";
    } else {
      // If the current logged-in user has no admin or staff role at the selected location
      // Assign the user role
      role.value = "User";
    }
  } else {
    // If the current logged-in user has no roles at the selected location
    // Assign an empty string as the role
    role.value = "";
  }
};

const bookingSlot = computed(() => {
  return {
    slotQty: selectedDurationSlot.value,
    sessionPrice: selectedActivity.value?.sessionFullPrice,
  };
});

/**
 * Fetches due and discount data for the payment step.
 * @returns Promise with booking calculation data
 */
const fetchPaymentStepData = async () => {
  try {
    isLoading.value = true;

    if(!form.value.time || !form.value.date || !selectedActivity.value || !form.value.duration){
      return
    }

    // Validate activity has required properties
    if (!selectedActivity.value.sessionFullPrice || !selectedActivity.value.sessionDuration) {
      console.error('Activity missing required properties:', selectedActivity.value);
      throw new Error('Activity is missing required pricing information');
    }

    // Make the API call
    const response = await pb.send('/bbapi/bookings/getBookingDue', {
      method: 'POST',
      body: {
        membership: !isAdmin.value ? userMembershipDiscounts.value : {},
        user: !isAdmin.value ? pb.authStore.model : null,
        activity: selectedActivity.value,
        date: form.value.date,
        time: form.value.time,
        duration: mapSelectionToMinutes(String(form.value.duration)?.replace(":", ".")),
        options: form.value.selectedOptions || [],
        discountCodeBookings: discountCodeData.value?.discountBookings || 0,
        discountCodeProducts: discountCodeData.value?.discountProduct || 0,
        docketBookingDiscount: 0,
        docketProductDiscount: 0,
        alreadyPaid: editBooking.value?.paid || 0,
        extraMinutes: 0,
      },
      requestKey: null,
    });
    console.log('API response for booking due:', response);

    // Handle the response
    // Check if API returned an error
    if (response.success === false) {
      throw new Error(response.error || 'Unknown API error');
    }

    todAdjustment.value = response.todAdjustment;
    todSource.value = response.todSource || "";
    docketDiscount.value = response.docketDiscount;
    discountApplied.value = response.discountApplied;
    lineDiscountTotal.value = response.lineDiscountTotal;
    dueAmount.value = response.due;
    bookingTotal.value = response.total;
  } catch (error: any) {
    console.error('Error fetching payment step data:', error);
    const errorMessage = error?.message || error?.response?.message || 'Failed to load payment information. Please try again.';
    handleUpdateErrorMessage(
      true,
      errorMessage
    );
    throw error;
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handles the submission of the booking form.
 * Validates all forms, updates the booking details, and makes an API call to update the booking.
 *
 * @returns {Promise<void>}
 */
const handleSubmit = async () => {
  // Validate all forms
  const valid = form.value.agreement;
  const durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );

  localStorage.removeItem("pendingBookingId")

  if (!valid) {
    return;
  }

  // Record T&C acceptance date only when the booking is for the logged-in user
  if (props.userId && form.value?.owner === props.userId) {
    pb.collection("users").update(props.userId, { acceptedTCs: moment().toISOString() }).catch(console.error);
  }

  selectedDurationSlot.value = durationTotal / getBookingSpacing.value;

  // Fetch payment data before proceeding
  await fetchPaymentStepData();

  const bookingDetails = {
    ...form.value,
    date: form.value?.date,
    due: dueAmount.value,
    value: bookingTotal.value,
    discountApplied: discountApplied.value,
    lineDiscountTotal: lineDiscountTotal.value,
    todAdjustment: todAdjustment.value,
    todSource: todSource.value,
    docketDiscount: docketDiscount.value,
    id: pendingBookingId.value || form.value.id,
    updatedBy: props?.loggedUser?.id || "",
    createdBy: props?.loggedUser?.id || "",
    duration: durationTotal,
    actualStartTime: form.value?.actualStartTime
      ? moment(form.value?.actualStartTime, "DD-MM-YYYY hh:mm a").isValid()
        ? moment(
            form.value?.actualStartTime,
            "DD-MM-YYYY hh:mm a"
          ).toISOString()
        : null
      : null,
    actualDuration: editBooking.value?.actualDuration
      ? form.value?.actualDuration
      : null,
    slot: selectedDurationSlot.value,
    sessionPrice: selectedActivity?.value?.sessionFullPrice,
    recurring: {},
    options: form.value.selectedOptions || [],
  };

  if (props?.loggedUser) {
    bookingDetails.owner = !isAdmin.value ? props?.loggedUser?.id : "";
    bookingDetails.members = !isAdmin.value ? [props?.loggedUser?.id] : [];
  }

  try {
    isLoading.value = true;
    await updateBooking(bookingDetails);

    if (!errorDateList.value.length) {
      // Goto payment tab
      currentStep.value = bookingSteps?.value?.length - 1;
    }
  } catch (e: any) {
    const { message } = e?.response;
    if (message) {
      handleUpdateErrorMessage(true, message);
    }
  } finally {
    isLoading.value = false;
  }
};


const removeConfirmedBooking = async (booking:any) => {
  pendingBookingId.value = form?.value?.id
  if(pendingBookingId.value) {
    try {
      await pb.collection("bookings").update(pendingBookingId.value, {state: 'Pending'});
    } catch(e){}
  }
  await confirmCloseBooking()

  try {
    window.location = window.location.pathname
  } catch(e){}

}
const confirmCloseBooking = async (cancelReason = "User Cancelled") => {
  const cancelledAtStep = bookingSteps.value[currentStep.value]?.title;
  await removeExistingPendingBooking(
    pendingBookingId.value,
    cancelReason,
    cancelledAtStep
  );

  // Clear the form data
  form.value = {
    id: "",
    title: "",
    date: moment().format("YYYY-MM-DD"),
    activity: "",
    guests: [],
    members: [],
    area: null,
    time: null,
    duration: 1,
    actualStartTime: null,
    notes: "",
    visitorEmail: "",
    visitorPhone: "",
    owner: !isAdmin.value ? props?.userId : "",
    location: "",
    actualDuration: null,
    selectedOptions: [],
    agreement: false,
    numberInParty: 1,
  };

  // Reset the current step to the first step
  currentStep.value = props.initialStep;

  // Close the booking dialog
  showPopup.value = false;

  // Clear the pending booking ID
  pendingBookingId.value = null;

  // Clear the error date list
  errorDateList.value = [];

  // Close the booking warning dialog
  isBookingWarningDialogVisible.value = false;
  discountCode.value = "";
  // Emit the closeDialog event with false as the parameter
  emit("closeDialog", false);
};

const closeDialog = async () => {
  if (
    pendingBookingId.value &&
    bookingSteps?.value?.length - 1 !== currentStep?.value
  ) {
    // show the confirmation message
    isCancelDialogVisible.value = true;
  } else {
    await confirmCloseBooking();
  }
};

const closeDialogForPayLater = async () => {
  const bookingId = form.value.id || pendingBookingId.value
  localStorage.removeItem('qbid')
  if(bookingId && (currentLocationDetails.value?.payInStoreEnabled || canEditDateAndTimePage())){
    localStorage.setItem('qbid',bookingId)
    try {
      form.value.state = "Confirmed"
      editBooking.value.state = "Confirmed"
      let getBookingDetails = await pb.collection("bookings").getOne(bookingId)
      if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
        await pb.collection("bookings").update(bookingId, {state: "Confirmed"})
      }
    } catch(e){
      console.error('closeDialogForPayLater error',e)
    }
  }

  if (bookingSteps?.value?.length - 1 === currentStep?.value) {
    await handleGetBooking(bookingId);
  } else {
    await closeDialog();
  }
};

/**
 * Handles the navigation to the next step in the booking process.
 * Validates the current step's form inputs, updates the current step,
 * and handles the creation or update of bookings.
 *
 * @returns {Promise<void>}
 */
const goToNextStep = async () => {
  // A second tap before the slot reservation returns would create a duplicate booking.
  if (isAdvancing.value) return;
  isAdvancing.value = true;
  try {
    await advanceStep();
  } finally {
    isAdvancing.value = false;
  }
};

const advanceStep = async () => {
  let isValid = false;
  const currentStepId = bookingSteps.value[currentStep.value]?.id;

  switch (currentStepId) {
    case 1: // Activity Step
      isValid = await validateActivity();
      break;
    case 2: // DateTime Step
      isValid = await validateDateTime();
      break;
    case 3: // Members Step
      isValid = await validateMembers();
      break;
    case 6: { // Number In Party Step
      const result = await numberInPartyFormRef.value?.validate();
      isValid = result?.valid ?? false;
      break;
    }
    case 4: // Agreement Step
      isValid = form.value.agreement;
      break;
    default:
      isValid = true;
  }

  if (isValid) {
    if (currentStepId === 2) {
      try {
        // Fetch payment data to get the correct due amount
        await fetchPaymentStepData();

        // Make a request to reserve the booking slot
        if(form?.value?.id && !pendingBookingId.value){
          pendingBookingId.value = form?.value?.id
        }

        const response = await reserveSlot(
          form.value?.duration,
          form.value?.date,
          form.value?.time,
          form.value?.activity,
          form.value?.area,
          !isAdmin.value ? props?.userId : "",
          props?.userId ? props?.userId : "",
          pendingBookingId.value,
          dueAmount.value,
          [],
          props?.isQuickBooking ? "Quick User Booking" : "Guest Booking",
          undefined,
          isAdmin.value
        );
        if (response) {
          // Update the pending booking ID
          pendingBookingId.value = response;
          localStorage.setItem("pendingBookingId", response);
          form.value.id = response;

          try {
            bookingProp.value = await pb.collection("bookings").getOne(response, {expand:'docketLine'});
            if(bookingProp.value.expand.docketLine?.docket) {
              bookingProp.value.docketId = bookingProp.value.expand.docketLine?.docket
            }
          } catch(e){}

          // Keep Selected Options
          let bookingHistory = await pb.collection("bookings").getOne(form.value.id)
          if(bookingHistory && bookingHistory?.options){
            form.value.selectedOptions = bookingHistory?.options
          }

          // Move to the next step
          currentStep.value++;
          // Clear the error message
          handleUpdateErrorMessage(false, "");
        }
      } catch (e: any) {
        const { message } = e?.response;
        if (message) {
          handleUpdateErrorMessage(true, message);
        }
      }
    } else if (currentStepId === 3 && pendingBookingId.value) {
      const userCount = (form.value?.members?.length || 0) + (form.value?.guests?.length || 0);
      form.value.numberInParty = userCount > 0 ? userCount : 1;
      await pb.collection("bookings").update(pendingBookingId.value, {
        visitorEmail: form.value.visitorEmail,
        visitorPhone: form.value.visitorPhone,
        visitors: form.value.guests.join(","),
        options: form.value.selectedOptions,
        notes: form.value.notes,
        numberInParty: form.value.numberInParty,
      });
      currentStep.value++;
    } else if (currentStepId === 6 && pendingBookingId.value) {
      await pb.collection("bookings").update(pendingBookingId.value, {
        numberInParty: form.value.numberInParty,
      });
      currentStep.value++;
    } else {
      currentStep.value++;
    }
  }
};

const {
  resetInactivityTimer,
  continueBooking,
  startInactivityTimer,
  clearInactivityTimer,
  clearPendingTimer,
} = useInactivityTimer(
  () => form.value?.id || pendingBookingId.value,
  showPopup,
  () => confirmCloseBooking('Timed out'),
  () => !editBooking.value?.state || editBooking.value?.state === 'Pending'
);

// Watch booking prop
watch(
  () => editBooking.value,
  (newValue) => {
    if (newValue && !isEmpty(newValue)) {
      // Clone the booking object to avoid direct mutations
      const bookingClone = { ...editBooking.value };

      // Assign values from booking to form fields
      docketId.value = bookingClone.docketId;
      docketLineId.value = bookingClone.docketLine;
      form.value = {
        id: bookingClone.id,
        title: bookingClone.title,
        activity: bookingClone.activity,
        area: bookingClone.area,
        date: moment(bookingClone.startDate).format("YYYY-MM-DD"),
        time: bookingClone.startTime
          ? moment(bookingClone.startTime).format("HH:mm")
          : null,
        members: bookingClone.members || bookingClone.Users,
        notes: bookingClone.notes,
        visitorEmail: bookingClone.visitorEmail,
        visitorPhone: bookingClone.visitorPhone,
        owner: bookingClone.owner || "",
        selectedOptions: bookingClone.selectedOptions || [],
        guests: bookingClone.visitors
          ? bookingClone.visitors
              ?.split(",")
              ?.map((item: string) => item.trim())
          : null,
        location: bookingClone.location,
        actualDuration: bookingClone.actualDuration,
        actualStartTime: bookingClone.actualStartTime,
        duration:
          getBookingSpacing.value === 60
            ? bookingClone.duration / getBookingSpacing.value
            : getDuration(bookingClone.duration, getBookingSpacing.value),
      };

      // Handle "in-progress" status updates
      if (bookingClone.status === "inprogress") {
        form.value.actualStartTime = bookingClone.actualStartTime
          ? moment(bookingClone.actualStartTime).format("DD-MM-YYYY hh:mm a")
          : null;
        form.value.actualDuration = bookingClone.actualDuration || null;
      }

      // Assign booking to pendingBookingId if its state is "Pending"
      if (bookingClone.state === "Pending")
        pendingBookingId.value = bookingClone.id;

    } else {
      // Reset form for new booking creation
      form.value = {
        id: "",
        date: moment().format("YYYY-MM-DD"),
        duration: 1,
        time: null,
        activity: "",
        title: "",
        actualStartTime: null,
        actualDuration: null,
        guests: [],
        members: [],
        area: null,
        notes: "",
        visitorEmail: "",
        visitorPhone: "",
        owner: !isAdmin.value ? props.userId : "",
        selectedOptions: [],
        numberInParty: 1,
      };
      currentStep.value = props.initialStep;
      showPopup.value = false;
      pendingBookingId.value = null;
    }
  },
  { immediate: true }
);

/**
 * Watch function to handle changes in form fields related to activity to update maxPlayers value
 *
 * @listens {[form.value.activity]}
 * @param newActivityId:string.
 * @emits {getMaxActivityVisitors()} - Calls the getMaxActivityVisitors function when the new activity changes.
 */
watch(
  () => [
    form.value.activity,
  ],
  ([newActivityId]) => {
    nextTick(() => {
      // Get maximum activity visitors when activity changes
      if (newActivityId) maxPlayers.value = getMaxActivityVisitors(activityList.value, newActivityId);
    });
  }
);

/**
 * Watch function to fetch payment step data when key form values change.
 * This ensures the booking due amount and discounts are always up to date.
 */
watch(
  [
    () => form.value.duration,
    () => form.value.owner,
    () => form.value.time,
    () => form.value.date,
    () => form.value.selectedOptions
  ],
  async (newValues, oldValues) => {
    if(props?.isDrawerOpen && form.value.activity && form.value.area){
      // If owner changed, wait for membership discounts to update first
      if(newValues[1] !== oldValues?.[1]) {
        userMembershipDiscounts.value = await getMembershipDiscounts(form.value?.owner, true);
        userHasCoupons.value = await doesUserHaveCoupons(form.value.owner, afterHours.value, form.value.activity)
      }
      await fetchPaymentStepData();
    }
  }
);

/**
 * Watch function to handle changes in the globalErrorMessage.value.
 * It clears the error message after 2 seconds if it is visible.
 *
 * @listens {globalErrorMessage.value}
 *
 * @param {Object} newError - The new value of the globalErrorMessage.value.
 * @param {boolean} newError.isVisible - Indicates whether the error message is visible.
 *
 * @emits {handleUpdateErrorMessage(false, "", "", "")} - Calls the function to clear the error message.
 */
watch(
  () => globalErrorMessage.value,
  (newError) => {
    if (newError?.isVisible) {
      // clear after 2 sec
      setTimeout(() => {
        handleUpdateErrorMessage(false, "", "", "");
      }, 2000);
    }
  },
  { immediate: true }
);

/**
 * Watch function to handle changes in the isLeftHandWarning.value.
 * It checks if a left-handed warning should be displayed and updates the error message accordingly.
 *
 * @listens {isLeftHandWarning.value}
 *
 * @param {boolean} newValue - The new value of the isLeftHandWarning.value.
 *
 * @emits {handleUpdateErrorMessage(true, "A member is left handed but this bay cannot do left handed", "warning", "tabler-alert-triangle")}
 * - Calls the function to update the error message when a left-handed warning should be displayed.
 */
watch(
  () => isLeftHandWarning.value,
  (newValue) => {
    // red message is higher priority so check before show warning messages
    if (
      newValue &&
      bookingSteps.value?.length - 1 !== currentStep.value &&
      globalInfoMessage?.value?.color !== "error"
    ) {
      handleUpdateErrorMessage(
        true,
        "A member is left handed but this bay cannot do left handed",
        "warning",
        "tabler-alert-triangle"
      );
    }
  },
  { immediate: true }
);

const gotoFood = () => {
  emit("closeDialog", false);
  router.push('/webshop?b=' + editBooking.value?.id)
}

/**
 * Watch to handle drawer open/close, resetting form data,
 * and managing timers for inactivity.
 *
 * @listens {props.isDrawerOpen} - Listens to the drawer open/close state.
 * @listens {isEditBooking.value} - Listens to the edit booking state.
 * @listens {isPendingBooking.value} - Listens to the pending booking state.
 * @listens {showPopup.value} - Listens to the show popup state.
 * @listens {getAreaList.value} - Listens to the area list state.
 *
 * @emits {currentStep.value} - Emits the current step value.
 * @emits {clearPendingTimer()} - Emits the function to clear the pending timer.
 * @emits {isCancelDialogVisible.value} - Emits the cancel dialog visibility state.
 * @emits {isPendingBooking.value} - Emits the pending booking state.
 * @emits {docketId.value} - Emits the docket ID value.
 * @emits {docketLineId.value} - Emits the docket line ID value.
 * @emits {pendingBookingId.value} - Emits the pending booking ID value.
 * @emits {resetInactivityTimer()} - Emits the function to reset the inactivity timer.
 * @emits {startInactivityTimer()} - Emits the function to start the inactivity timer.
 * @emits {alternativeAreaList.value} - Emits the alternative area list value.
 */
watch(
  () => props?.isDrawerOpen,
  async (newValue) => {
    if (!newValue) {
      currentStep.value = props.initialStep;
      clearPendingTimer();
      isCancelDialogVisible.value = false;
      isPendingBooking.value = false;
      docketId.value = "";
      docketLineId.value = "";
      pendingBookingId.value = null;
      isLeftHandedBookingErrorVerified.value = false;
      discountCode.value = "";
      discountCodeData.value = {};
      userMembershipDiscounts.value = {};
      return;
    }

    if (editBooking.value?.docketId) {
      // fetch docket data
      const docket = await pb.collection("dockets").getOne(editBooking.value?.docketId);
      discountCode.value = docket?.discountCode;
    }
    await getUserCreditCards();
    userMembershipDiscounts.value = await getMembershipDiscounts(form.value?.owner, true);
  },
  { immediate: true }
);

watchEffect(() => {
  if (!isEditBooking.value && isPendingBooking.value) {
    resetInactivityTimer();

    const resetEvents = ["mousemove", "keydown", "touchstart", "click"];

    resetEvents.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );
  }

  if (!showPopup.value) clearInactivityTimer();
  else startInactivityTimer();

  if (getAreaList.value) {
    alternativeAreaList.value = isLeftHandWarning?.value
      ? getAreaList.value?.filter((el) => el?.canLeftHanded)
      : getAreaList.value || [];
  }

});

// Validation methods
const validateDateTime = async () => {
  return await dateTimeStepRef.value?.validate();
};

const validateMembers = async () => {
  return await membersStepRef.value?.validate();
};

const validateActivity = async () => {
  return await activityStepRef.value?.validate();
};

const getCouponValue = () => {
  let couponValue = props?.couponValue || 30
  if(selectedActivity?.value?.sessionFullPrice && selectedActivity?.value?.couponCost) {
    if(form.value?.time && afterHours.value){
      couponValue = selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost
    } else {
      couponValue = selectedActivity?.value?.couponCost
    }
  }
  return couponValue;
}

const getCouponCost = () => {
  let couponCost = selectedActivity?.value?.couponCost || 1
  if(selectedActivity?.value?.couponCost) {
    if(form.value?.time && afterHours.value){
      couponCost = selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost
    } else {
      couponCost = selectedActivity?.value?.couponCost
    }
  }
  return couponCost;
}

onMounted(async () => {
  if (props.userId) {
    const currentUser = await pb.collection("users").getOne(`${props.userId}`)
    isAdmin.value = currentUser && currentUser.roles.length > 0 && !isUserMode.value ? true : false;
    form.value.owner = !isAdmin.value ? props.userId : "";
  }
  userMembershipDiscounts.value = await getMembershipDiscounts(form.value?.owner, true)
  userHasCoupons.value = await doesUserHaveCoupons(form.value.owner, afterHours.value, form.value.activity)
  bookingProp.value = {};
  discountCode.value = "";
  assignRole();
  if (activityList.value.length == 1) {
    await nextTick(async () => {
      form.value.activity = activityList.value[0].id;
      currentStep.value++
    });
  }
  // BayMode: pre-select the activity & bay matching defaultAreaNumber, then skip activity step
  if (props.defaultArea != null) {
    const matchedArea = props.areaList?.find(
      (area: any) => area.id === props.defaultArea
    );
    if(matchedArea?.activities?.length == 1) {
      form.value.activity = matchedArea.activities[0];
      currentStep.value = 1;
    }
  }
  const { dpid, sessionId } = route?.query;
  await getUserCreditCards()
  await checkIfFoodCanBeOrdered()
  if (!dpid) return;

  // Define status messages mapping
  const statusMessages: any = {
    Canceled: { type: "cancelled", message: "Payment Cancelled" },
    Declined: { type: "declined", message: "Payment Declined" },
    Waiting: { type: "error", message: "Payment Waiting" },
    Pending: { type: "error", message: "Server not responding" },
    Busy: { type: "error", message: "Terminal is Busy" },
    Offline: { type: "offline", message: "Terminal Offline" },
    Approved: { type: "success", message: "Payment Approved" },
    Error: { type: "error", message: "Payment Gateway Error" },
    GotWebResponse: { type: "info", message: "Waiting for server response" },
  };

  let status = "";
  let payment: any;
  let startTime = Date.now();
  const TIMEOUT = 60000; // 60 seconds timeout
  const POLL_INTERVAL = 2000; // 2 seconds interval

  // Poll until we get a final status or timeout
  while (true) {
    // Check for timeout
    if (Date.now() - startTime > TIMEOUT) {
      processingPayment.value = {
        isVisible: true,
        message: "Payment request timed out",
        type: "error",
        spinner: false,
      };
      break;
    }

    try {
      payment = await pb.collection("docketPayments").getOne(dpid, {
        expand: "docket",
      });
      status = payment.status;
      surchargeAmount.value = payment.surchargeAmount || 0;
      // Update processing payment status
      if (status in statusMessages) {
        const { type, message } = statusMessages[status];
        processingPayment.value = {
          isVisible: true,
          message,
          type,
          spinner: ["Pending", "GotWebResponse", "Waiting"].includes(status),
        };
      }

      // Handle terminal redirect
      if (status === "Waiting" && !sessionId && payment.webRedirect) {
        window.location.href = payment.webRedirect;
        break;
      }

      // Handle final statuses
      if (
        [
          "Approved",
          "Canceled",
          "Declined",
          "Error",
          "Timeout",
          "Offline",
          "Busy",
        ].includes(status)
      ) {
        break;
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    } catch (error) {
      console.error("Error polling payment status:", error);
      processingPayment.value = {
        isVisible: true,
        message: "Error checking payment status",
        type: "error",
        spinner: false,
      };
      break;
    }
  }
  const { docket, amount } = payment;
  const response = await pb.collection("docketLines").getFullList({
    filter: `docket = '${docket}'`,
    expand: "booking",
  });
  docketBackId.value = docket;
  docketLineItems.value = response;
  processingPayment.value = {
    isVisible: true,
    message: `Payment ${status}`,
    type: status === "Approved" ? "success" : "error",
    spinner: false,
  };

  if (status === "Approved") {

    try {
      const docketRecord = await pb.collection("dockets").getOne(docket);

      const {expand} = await pb
        .collection("docketLines")
        .getFirstListItem(`docket = '${docket}' && transactionType = 'Booking Payment'`, {
          expand: "booking"
        });

      editBooking.value = expand?.booking;
      const bookingStatus = getBookingStatus(
        editBooking.value?.actualStartTime,
        editBooking.value?.actualDuration
      );
      editBooking.value.status = bookingStatus;
      editBooking.value.docketId = docket;
      editBooking.value.selectedOptions = expand?.booking?.options;
      docketId.value = docket;
      editBooking.value.startDate = editBooking.value?.startTime;
      bookingProp.value = editBooking.value;
      email.value = expand?.booking?.visitorEmail || "";
      if (amount >= docketRecord?.due) {
        const booking = await pb.collection("bookings").update(`${expand?.booking?.id}`, {
          paid: (expand?.booking?.paid || 0) + (amount ?? 0),
          due: 0,
        });

        if(booking && booking.state != 'Confirmed') {
          await pb.collection("bookings").update(`${expand?.booking?.id}`, {state: 'Confirmed'});
        }
      }
      currentStep.value = bookingSteps.value.length - 1;
    } catch (error) {
      console.error("Error updating docket or booking:", error);
    }

  } else {
    currentStep.value = bookingSteps.value.length - 1;
    const bookingLine = response?.find((el: any) => el?.booking);

    if(!pb.authStore?.model?.id && bookingLine?.booking && bookingLine?.expand?.booking.state != "Confirmed"){
      await pb.collection("bookings").update(bookingLine?.booking, {state:"Pending"})
    }
  }

  // New - get the booking
  if(dpid) {
    try {
      let paymentDocketCheck = await pb.collection("docketPayments").getOne(dpid);
      let DocketLinesCheck = await pb.collection("docketLines").getFirstListItem('docket="' + paymentDocketCheck.docket + '" && booking != ""');
      let bookingDocketCheck = await pb.collection("bookings").getOne(`${DocketLinesCheck.booking}`)
      localStorage.setItem('qbid',DocketLinesCheck.booking)
      editBooking.value = bookingDocketCheck;
      editBooking.value.selectedOptions = bookingDocketCheck.options;
      editBooking.value.docketId = paymentDocketCheck.docket;
      editBooking.value.startDate = editBooking.value?.startTime;

      bookingProp.value = editBooking.value;

      discountCodeData.value = {
        discountBookings: payment?.expand?.docket?.discountBookings,
        discountProduct: payment?.expand?.docket?.discountProducts,
      };
    } catch(e){
      console.error('e',e)
    }
  }
  await getUserCreditCards()
});


const canConfirm = () => {
  if(!isAdmin.value && !!props?.loggedUser?.id){
    return true
  } else {
    if(currentLocationDetails.value?.guestValidateEmail && LocalEmailValidator(form.value.visitorEmail) != true){
      return false
    }
    if(currentLocationDetails.value?.guestValidateSMS && LocalMobileValidator(form.value.visitorPhone) != true){
      return false
    }
  }
  return true
}

const LocalEmailValidator = (value: unknown) => {
  if (isEmpty(value)) return currentLocationDetails.value?.guestValidateEmail ? 'You must provide a valid email' : true;
  return emailValidator(value);
};

const LocalMobileValidator = (value: unknown) => {
  return mobileValidator(value, currentLocationDetails.value?.guestValidateSMS);
};

const canPayInStore = () => {
  if(selectedActivity?.value?.expand?.availibleTimes && startDate.value) {
    const timeRanges = selectedActivity?.value?.expand?.availibleTimes;
    const dayOfWeek = moment(startDate.value).format("dddd").toLowerCase();

    const startTime = moment(startDate.value, "HH:mm");

    const availableHoursForDay = getAvailableHoursForDay(timeRanges.filter((tr: any) => tr.allUsers === true), dayOfWeek, startDate.value)

    // remove Last Time Slot
    if(availableHoursForDay.length > 1) {
      availableHoursForDay.pop()
    }
    // Check if the start and end time are within valid hours
    const isValidTimeForCurrentDay = availableHoursForDay.includes(startTime.hour())
    return (currentLocationDetails?.value?.payInStoreEnabled && isValidTimeForCurrentDay) || canEditDateAndTimePage()
  } else {
    return currentLocationDetails?.value?.payInStoreEnabled || canEditDateAndTimePage()
  }
}

const canEditDateAndTimePage = () => {
  return pb.authStore.model?.roles && pb.authStore.model?.roles.length > 0
}

/**
 * Watch for changes in membership discounts to refetch payment data
 */
watch(
  () => userMembershipDiscounts.value,
  async (newValue) => {
    // Fetch payment data after membership discounts are loaded
    if(newValue && props?.isDrawerOpen && form.value.activity && form.value.area) {
      await fetchPaymentStepData();
    }
  }
);
</script>

<template>
  <VDialog
    :model-value="props.isDrawerOpen"
    temporary
    persistent
    no-click-animation
    location="end"
    width="900"
    class="scrollable-content booking_stepper"
  >
    <VOverlay
      v-model="isLoading"
      contained
      persistent
      class="align-center justify-center"
    >
      <VProgressCircular indeterminate />
    </VOverlay>

    <!-- Timeout error dialog -->
    <TimeOutError
      :is-visible="showPopup"
      @closeDialog="closeDialog"
      @continueBooking="continueBooking"
    />

    <!-- leftHandedWarning dialog -->
    <WarningDialog
      :is-visible="isLeftHandedWarningDialogVisible"
      @closeDialog="
        ($event: any) => {
          isLeftHandedWarningDialogVisible = $event;
        }
      "
      @verifiedByUser="
        () => {
          isLeftHandedBookingErrorVerified = true;
          isLeftHandedWarningDialogVisible = false;
        }
      "
    />
    <VCard height="auto" flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor">
        <!-- 👉 Header -->
        <VCardTitle> Add <span v-if="form?.activity && activityList && activityList.length > 0">{{activityList.filter(a => a.id == form.activity).pop().name}}</span> Booking </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          :disabled="currentStep === bookingSteps.length - 1"
          @click="closeDialog"
        />
      </div>

      <!-- Stepper Edit Content  -->
      <VCardText>
        <VRow>
          <!-- large screen stepper -->
          <VCol cols="12" class="stepper-form-header">
            <CustomStepper
              v-model:current-step="currentStep"
              :items="bookingSteps"
              :is-active-step-valid="false"
              class="stepper-icon-step-bg"
              direction="horizontal"
            />
          </VCol>
          <VCol cols="12" class="pt-0">
            <VWindow v-model="currentStep" class="disable-tab-transition" :touch="false" disabled>
              <!-- Activity Step -->
              <VWindowItem >
                <ActivityStep
                  ref="activityStepRef"
                  v-model:form="form"
                  :activity-list="activityList"
                  :is-fields-disabled="false"
                  :is-edit-booking="isEditBooking"
                  :have-create-access="haveCreateAccess"
                  :have-edit-access="true"
                  @validate="validateActivity"
                  @next-step="currentStep++"
                />
              </VWindowItem>

              <!-- DateTime Step -->
              <VWindowItem>
                <DateTimeStep v-if="currentStep == 1"
                  ref="dateTimeStepRef"
                  v-model:form="form"
                  :role-list="props?.currentLoggedInRoles"
                  :activity-list="activityList"
                  :get-area-list="getAreaList"
                  :member-list="[]"
                  :membership="isAdmin ? [] : props?.membership"
                  :is-quick-booking="props?.isQuickBooking"
                  :role="role"
                  :allowedDatesRecurring="(date) => allowedDatesRecurring(date, getMaxBookAhead)"
                  :is-fields-disabled="false"
                  :area-duration-disable="false"
                  :is-edit-booking="isEditBooking"
                  :have-create-access="haveCreateAccess"
                  :have-edit-access="true"
                  :defaultArea="props.defaultArea"
                  @validate="validateDateTime"
                  @updateErrorMessage="handleDateTimeError"
                />
              </VWindowItem>

              <!-- Members Step -->
              <VWindowItem>
                <MembersStep v-if="currentStep == 2"
                  ref="membersStepRef"
                  v-model:form="form"
                  :is-quick-booking="props?.isQuickBooking"
                  :max-players="maxPlayers || 0"
                  :is-edit-booking="isEditBooking"
                  :member-list="memberList"
                  :is-fields-disabled="false"
                  :area-duration-disable="false"
                  :have-create-access="haveCreateAccess"
                  :have-edit-access="true"
                  :activityList="activityList"
                  :logged-user="props?.loggedUser"
                  :isAdmin="isAdmin"
                  @validate="validateMembers"
                  @updateErrorMessage="handleMembersError"
                />
              </VWindowItem>
              <VWindowItem v-if="bookingSteps?.some((el) => el.id === 6)">
                <NumberInPartyStep ref="numberInPartyFormRef" :form="form" />
              </VWindowItem>
              <VWindowItem>
                <AgreementStep
                  :selectedActivity="selectedActivity"
                  :isAdmin="isAdmin"
                  :isQuickBooking="isQuickBooking"
                  :form="form"
                />
              </VWindowItem>
              <VWindowItem>
                <PaymentForm v-if="currentStep == bookingSteps.length -1 && showPayment"
                  :have-create-permission="haveCreateAccess"
                  :total-due="dueAmount"
                  :products="selectedProducts"
                  :booking-id="form?.id"
                  :bookingProp="bookingProp"
                  :logged-in-user-id="props?.loggedUser.id"
                  :membership="userInfo?.expand?.membership"
                  :total-paid="editBooking?.paid || bookingProp?.paid || 0"
                  :lineDiscountTotal="lineDiscountTotal"
                  :coupon-value="getCouponValue()"
                  :coupon-cost="getCouponCost()"
                  :is-quick-booking="props?.isQuickBooking"
                  :location="selectedActivity?.location"
                  :todAdjustment="todAdjustment"
                  :todSource="todSource"
                  :discountProduct="userInfo?.expand?.membership?.discountProduct || 0"
                  :user-credit-cards="userCreditCards"
                  :discountBookings="docketDiscount"
                  :booking-discount="docketDiscount"
                  :discountEvents="userInfo?.expand?.membership?.discountEvents || 0"
                  :discountSources="[userInfo?.expand?.membership ? 'Membership' : '']"
                  :discountApplied="discountApplied"
                  :owner="props?.isQuickBooking ? userInfo.id : form?.owner"
                  :booking-amount="bookingTotal"
                  :bookingSlot="bookingSlot"
                  :payMethods="editBooking?.payMethod"
                  type="Booking"
                  :canPayInStore="canPayInStore()"
                  :source="
                    props?.isQuickBooking
                      ? 'Quick User Booking'
                      : 'Guest Booking'
                  "
                  :afterHours="afterHours"
                  @paymentTabChange="(tab) => selectedPaymentTab = tab"
                  @paymentSuccess="handlePaymentSuccess"
                  @bookingChange="handleGetBooking"
                  @updateMessage="
                    handleUpdateErrorMessage(
                      $event?.isVisible,
                      $event?.message,
                      $event?.color,
                      $event?.icon
                    )
                  "
                  @goToPosPayment="gotToPos"
                  @addNewCardDialog="emit('addNewCardDialog', true)"
                  @refetch="async () => { await refetchBooking(); await fetchPaymentStepData(); }"
                  @closeBooking="closeDialog"
                  @closeDialogForPayLater="closeDialogForPayLater"
                />
              </VWindowItem>
            </VWindow>
          </VCol>
        </VRow>
      </VCardText>

      <!-- Stepper Edit Content Ends -->
      <div class="bold_tag">
        <VAlert
          v-if="globalErrorMessage?.isVisible"
          :color="globalErrorMessage?.color || 'error'"
          :icon="globalErrorMessage?.icon || 'tabler-xbox-x'"
        >
          {{ globalErrorMessage?.message }}
        </VAlert>
        <VAlert
          v-else
          :color="globalInfoMessage.color"
          :icon="globalInfoMessage?.icon || 'mdi-info'"
        >
          <span v-if="globalInfoMessage?.due">${{globalInfoMessage?.due}}</span>
          <span v-if="globalInfoMessage?.todAdjustment">
            <v-tooltip activator="parent">
              Time Of Day Pricing {{ globalInfoMessage.todAdjustment > 0 ? '+' : '' }}{{ globalInfoMessage.todAdjustment }}%
            </v-tooltip>
            <v-icon icon="mdi-clock-outline" size="x-small" class="ml-1"></v-icon>
          </span>
          {{globalInfoMessage?.message.split(")")[0]}}
          <span>
             <v-tooltip activator="parent">
                The amount of Coupons required to fully cover the<br/> booked time (extras can't be paid with coupons)
              </v-tooltip>
            <v-icon v-if="globalInfoMessage?.message.indexOf(')') != -1" icon="mdi-info"></v-icon>
          </span>
          <span v-if="globalInfoMessage?.message.indexOf(')') != -1">) {{globalInfoMessage?.message.split(")")[1]}}</span>
        </VAlert>
        <VAlert v-if="canOrderFood && currentLocationDetails?.webshopEnabled && editBooking?.id && editBooking?.state == 'Confirmed'"  class="pt-2 pb-2">
          <div class="d-flex"><VSpacer></VSpacer><VBtn color="primary" prepend-icon="mdi-food" @click.prevent="gotoFood()">Order Food</VBtn></div>
        </VAlert>
      </div>
      <VCardText class="button_container">
        <div class="d-flex button_group flex-wrap gap-3">
          <div class="d-flex button_group__first_btn">
            <VBtn
              :disabled="currentStep === initialStep"
              color="secondary"
              variant="flat"
              @click.prevent="currentStep--"
            >
              <VIcon icon="mdi-arrow-left" start class="flip-in-rtl" />
              Previous
            </VBtn>
          </div>
          <div class="d-flex">
            <VBtn color="error" class="mt-xs-0 mt-sm-0" @click="isCancelDialogVisible = true"
                  v-if="currentStep === bookingSteps.length - 1">
              Delete
            </VBtn>
            <VBtn
              v-if="canPayInStore() && currentStep === bookingSteps.length - 1 && editBooking?.state != 'Confirmed' && selectedPaymentTab === 'payInStore'"
              variant="flat"
              color="success"
              class="ml-2"
              @click="closeDialogForPayLater"
            >
              Confirm - Pay Later
            </VBtn>

            <!-- create booking -->
            <div
              class="align-content-center"
              v-if="bookingSteps.length - 1 !== currentStep"
            >
              <VBtn
                v-if="bookingSteps.length - 2 > currentStep"
                class="ml-2"
                :disabled="isAdvancing"
                :loading="isAdvancing"
                @click="goToNextStep"
              >
                Next
                <VIcon icon="mdi-arrow-right" end class="flip-in-rtl" />
              </VBtn>
              <VBtn
                v-else
                :disabled="!canConfirm()"
                color="success"
                class="ml-2"
                @click="handleSubmit">
                Confirm
              </VBtn>
            </div>
          </div>
        </div>
      </VCardText>
    </VCard>

    <!-- Booking cancel confirmation dialog -->
    <BookingCancelConfirmDialog
      :is-open="isCancelDialogVisible"
      :activity="
        activityList?.find(
          (activity: any) => activity.id === bookingProp?.activity,
        )
      "
      @closeDialog="isCancelDialogVisible = $event"
      @cancelBooking="removeConfirmedBooking(props)"
    />

    <!-- Error date list dialog -->
    <ErrorWarnDatesDialog
      :is-open="isBookingWarningDialogVisible"
      :dates="errorDateList"
      @closeDialog="isBookingWarningDialogVisible = false"
    />
  </VDialog>

  <VDialog
    v-model="processingPayment.isVisible"
    persistent
    no-click-animation
    class="pair_terminal_dialog"
  >
    <VCard max-width="550">
      <div class="payment-header text-center">
        <!-- Main Payment Amount -->
        <span class="payment-amount text-h4" v-if="totalAmount > 1">
          Payment of {{ formatPrice(totalAmount) }}
        </span><span v-else class="payment-amount text-h4">Processing Payment</span>

        <!-- Payment Method Text -->
        <span class="payment-method">with Card</span>

        <!-- Surcharge Info with Background -->
        <span v-if="getSurchargeAmount" class="surcharge-info">
          ( including surcharge of {{ formatPrice(getSurchargeAmount) }} )
        </span>
      </div>

      <VCardText v-if="processingPayment.spinner" class="text-center">
        <VProgressCircular indeterminate size="64" width="8" />
      </VCardText>

      <!-- Payment Status Messages -->
      <VCardText class="text-center">
        <span
          v-if="processingPayment?.message"
          class="text-h4 rounded pa-2"
          :class="{
            'text-success': processingPayment?.type === 'success',
            'text-error': processingPayment?.type !== 'success',
          }"
        >
          {{ processingPayment?.message }}
        </span>

        <span
          v-if="
            processingPayment?.type === 'declined' &&
            processingPayment?.description
          "
          class="text-h3 rounded pa-2 text-error"
        >
          {{ processingPayment?.description }}
        </span>

        <!-- Success: Email option -->
        <VResponsive
          v-if="processingPayment?.type === 'success'"
          class="mx-auto mt-4"
          max-width="344"
        >
          <VForm ref="refVFormReceipt" class="d-flex">
            <VTextField
              v-model="email"
              :rules="[requiredValidator, emailValidator]"
              label="Email Address"
              class="mt-2"
            />
          </VForm>
          <div class="d-flex justify-center flex-wrap gap-4 mt-2">
            <VBtn color="primary" @click="() => validateAndPrint(emailReceipt)">
              Email Receipt
            </VBtn>
          </div>
        </VResponsive>
      </VCardText>

      <VCardText class="d-flex justify-end flex-wrap gap-4">
        <VBtn
          color="error"
          @click="
            async () => {
              processingPayment.isVisible = false;
              if (processingPayment?.type === 'success') {
                handleGetBooking(editBooking.id);
              }
            }
          ">
          Close
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.v-slide-group__content {
  padding: 0.625rem;
}

.stepper-icon-step {
  margin-block-start: 0.625rem;
}

.stepper-icon {
  border-radius: 0.3125rem;
  background-color: rgb(var(--v-theme-primary));
  color: rgba(var(--v-theme-on-primary));
}

.stepper-form-header {
  padding: 1.25rem;
  font-size: 0.625rem;
}

.member_list_selection {
  .v-field__input {
    max-block-size: 7.5rem;
    overflow-y: auto;
    scrollbar-width: thin;
  }
}

.button_group {
  display: flex;
  justify-content: space-between;
}
</style>
